import React from 'react';
import { AlertTriangle, XCircle, Info, Shield } from 'lucide-react';

const ConflictDetection = ({ conflicts }) => {
  if (!conflicts || conflicts.length === 0) {
    return null;
  }

  const getConflictIcon = (severity) => {
    switch (severity) {
      case 'error':
        return XCircle;
      case 'warning':
        return AlertTriangle;
      case 'info':
        return Info;
      default:
        return AlertTriangle;
    }
  };

  const getConflictStyle = (severity) => {
    switch (severity) {
      case 'error':
        return {
          container: 'bg-red-50 border-red-200',
          icon: 'text-red-600',
          title: 'text-red-800',
          message: 'text-red-700',
          header: 'Assignment Blocked'
        };
      case 'warning':
        return {
          container: 'bg-yellow-50 border-yellow-200',
          icon: 'text-yellow-600',
          title: 'text-yellow-800',
          message: 'text-yellow-700',
          header: 'Assignment Warning'
        };
      case 'info':
        return {
          container: 'bg-blue-50 border-blue-200',
          icon: 'text-blue-600',
          title: 'text-blue-800',
          message: 'text-blue-700',
          header: 'Assignment Info'
        };
      default:
        return {
          container: 'bg-gray-50 border-gray-200',
          icon: 'text-gray-600',
          title: 'text-gray-800',
          message: 'text-gray-700',
          header: 'Assignment Notice'
        };
    }
  };

  const getConflictDescription = (type) => {
    switch (type) {
      case 'already_assigned':
        return 'This driver is currently assigned to another vehicle. Reassigning will unassign them from their current vehicle.';
      case 'unavailable':
        return 'This driver is currently unavailable and cannot be assigned to any vehicle.';
      case 'license_mismatch':
        return 'Driver\'s license class does not meet the requirements for this vehicle type.';
      case 'experience_insufficient':
        return 'Driver does not meet the minimum experience requirements for this vehicle.';
      case 'safety_score_low':
        return 'Driver\'s safety score is below the recommended threshold for this vehicle.';
      case 'schedule_conflict':
        return 'Driver has a scheduling conflict that may affect this assignment.';
      default:
        return 'There is a potential issue with this assignment that requires review.';
    }
  };

  const getSolutionSuggestion = (type) => {
    switch (type) {
      case 'already_assigned':
        return 'Consider if reassignment is necessary or select a different driver.';
      case 'unavailable':
        return 'Please select a different driver or wait until this driver becomes available.';
      case 'license_mismatch':
        return 'Select a driver with the appropriate license class or update driver credentials.';
      case 'experience_insufficient':
        return 'Consider assigning a more experienced driver or provide additional training.';
      case 'safety_score_low':
        return 'Review driver\'s safety record and consider additional training if needed.';
      case 'schedule_conflict':
        return 'Review driver\'s schedule and adjust assignment timing if necessary.';
      default:
        return 'Please review the assignment details and make necessary adjustments.';
    }
  };

  // Group conflicts by severity
  const errorConflicts = conflicts.filter(c => c.severity === 'error');
  const warningConflicts = conflicts.filter(c => c.severity === 'warning');
  const infoConflicts = conflicts.filter(c => c.severity === 'info');

  return (
    <div className="space-y-4">
      {/* Error Conflicts - Blocking */}
      {errorConflicts.length > 0 && (
        <div className="border rounded-lg p-4 bg-red-50 border-red-200">
          <div className="flex items-start space-x-3">
            <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-800 mb-2">
                Assignment Blocked ({errorConflicts.length})
              </h4>
              <div className="space-y-3">
                {errorConflicts.map((conflict, index) => (
                  <div key={index} className="bg-white rounded-md p-3 border border-red-200">
                    <p className="text-sm font-medium text-red-900 mb-1">
                      {conflict.message}
                    </p>
                    <p className="text-xs text-red-700 mb-2">
                      {getConflictDescription(conflict.type)}
                    </p>
                    <p className="text-xs text-red-600 flex items-start space-x-1">
                      <Shield className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      <span>{getSolutionSuggestion(conflict.type)}</span>
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-xs text-red-700 font-medium">
                ⚠️ These issues must be resolved before assignment can proceed.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Warning Conflicts - Proceed with caution */}
      {warningConflicts.length > 0 && (
        <div className="border rounded-lg p-4 bg-yellow-50 border-yellow-200">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-yellow-800 mb-2">
                Assignment Warnings ({warningConflicts.length})
              </h4>
              <div className="space-y-3">
                {warningConflicts.map((conflict, index) => (
                  <div key={index} className="bg-white rounded-md p-3 border border-yellow-200">
                    <p className="text-sm font-medium text-yellow-900 mb-1">
                      {conflict.message}
                    </p>
                    <p className="text-xs text-yellow-700 mb-2">
                      {getConflictDescription(conflict.type)}
                    </p>
                    <p className="text-xs text-yellow-600 flex items-start space-x-1">
                      <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      <span>{getSolutionSuggestion(conflict.type)}</span>
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-xs text-yellow-700">
                ⚠️ You can proceed with assignment, but please review these warnings.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Conflicts - FYI */}
      {infoConflicts.length > 0 && (
        <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
          <div className="flex items-start space-x-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-blue-800 mb-2">
                Assignment Information ({infoConflicts.length})
              </h4>
              <div className="space-y-2">
                {infoConflicts.map((conflict, index) => (
                  <div key={index} className="text-sm text-blue-700">
                    • {conflict.message}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Impact Summary */}
      {conflicts.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <h5 className="text-xs font-semibold text-gray-800 mb-2">Assignment Impact:</h5>
          <div className="grid grid-cols-3 gap-4 text-xs">
            <div className="text-center">
              <div className="text-red-600 font-semibold">{errorConflicts.length}</div>
              <div className="text-gray-600">Blocking Issues</div>
            </div>
            <div className="text-center">
              <div className="text-yellow-600 font-semibold">{warningConflicts.length}</div>
              <div className="text-gray-600">Warnings</div>
            </div>
            <div className="text-center">
              <div className="text-blue-600 font-semibold">{infoConflicts.length}</div>
              <div className="text-gray-600">Info Items</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConflictDetection;